import { useAppSelector } from '@/stores/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CenterListItem from './list/CenterListItem';
import styled from 'styled-components';
import { colors } from '@/theme/colors';
import { KeywordItemType } from '@/api/type/commonTypes';
import { useRouterQuery } from '@/hook/router-query/useRouterQuery';
import { useLoading } from '@/context/LoadingContenxt';
import CommonTitle from '@/components/title/CommonTitle';
import CommonSearch from '@/components/search/CommonSearch';
import Pagination from '@/components/pagination/Pagination';
import { getCenterList } from '@/api/partner-list/center/getCenterList';
import { partner } from '@/stores/slices/partner-list/partnerSlice';
import { useTranslation } from 'react-i18next';

const keywordTypeData: KeywordItemType[] = [
  {
    keyword: 'LOGIN_ID',
    i18nKey: 'common.loginId',
  },
  {
    keyword: 'CENTER_HEAD_NAME',
    i18nKey: 'common.representativeName',
  },
  {
    keyword: 'CENTER_NAME',
    i18nKey: 'centerList.centerName',
  },
  {
    keyword: 'REGISTERED_AT',
    i18nKey: 'common.registeredDate',
  },
];

const SIZE = 30;
const CenterListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page') ?? 1);
  const size = Number(searchParams.get('size') ?? SIZE);
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedKeywordType, setSelectedKeywordType] =
    useState<KeywordItemType>(keywordTypeData[0]);
  const dispatch = useDispatch();
  const keywordType = searchParams.get('keywordType');
  const { centerList } = useAppSelector((state) => state.partner);
  const { startLoading, stopLoading } = useLoading();
  const { updateQuery } = useRouterQuery();
  const searchValueRef = useRef('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  /** 목록 fetch 함수 */
  const fetchCenterList = useCallback(async () => {
    startLoading();
    try {
      const res = await getCenterList({
        keywordType: keywordType ?? 'LOGIN_ID',
        keyword: searchValueRef.current,
        page: currentPage,
        size: SIZE,
      });
      if (res.isSuccess) {
        dispatch(partner.setCenterList(res.data));
      }
    } finally {
      stopLoading();
    }
  }, [currentPage, dispatch, keywordType, startLoading, stopLoading]);

  /** 페이지 변경 */
  const handlePageChange = (page: number) => {
    setSearchParams({
      page: String(page),
      size: String(size),
    });
  };

  /** 검색 value 변경 */
  const onChangeSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      searchValueRef.current = e.target.value;
    },
    []
  );

  /** 검색 아이콘 클릭 및 엔터 */
  const onClickSearch = useCallback(async () => {
    startLoading();
    try {
      const res = await getCenterList({
        keywordType: selectedKeywordType.keyword,
        keyword: searchValueRef.current,
        page: 1,
        size: SIZE,
      });
      if (res.isSuccess) {
        dispatch(partner.setCenterList(res.data));
        updateQuery({
          params: {
            keywordType: selectedKeywordType.keyword,
            keyword: searchValueRef.current,
            page: 1,
            size: SIZE,
          },
          route: '/partner/center-list',
        });
      } else {
        if (res.error.code === 'E_DATE_001') {
          alert(t('common.searchPlaceholder'));
        }
      }
    } finally {
      stopLoading();
    }
  }, [
    dispatch,
    selectedKeywordType.keyword,
    startLoading,
    stopLoading,
    t,
    updateQuery,
  ]);

  /** 검색 초기화 */
  const onClickSearchReset = useCallback(() => {
    setSearchParams({
      page: String(1),
      size: String(size),
    });
    setSearchValue('');
    searchValueRef.current = '';
    fetchCenterList();
    navigate('/partner/center-list');
  }, [fetchCenterList, navigate, setSearchParams, size]);

  /** keywordType 저장 */
  const onDispatchKeyword = useCallback((keywordType: KeywordItemType) => {
    setSelectedKeywordType(keywordType);
  }, []);

  useEffect(() => {
    fetchCenterList();
  }, [fetchCenterList]);

  console.log('centerList', centerList);
  if (!centerList) return;

  return (
    <Container>
      <CommonTitle
        title={t('centerList.title')}
        count={centerList.totalElements}
      />
      <SearchWrapper>
        <CommonSearch
          value={searchValue}
          onChange={onChangeSearch}
          onClickSearch={onClickSearch}
          onClickReset={onClickSearchReset}
          options={keywordTypeData}
          onSelect={onDispatchKeyword}
          selectedOption={selectedKeywordType}
        />
      </SearchWrapper>
      <ListWrapper>
        <CenterListItem items={centerList.items} />
      </ListWrapper>
      <Pagination
        totalItems={centerList.totalElements}
        itemsPerPage={SIZE}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </Container>
  );
};

export default CenterListPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SearchWrapper = styled.div`
  padding: 20px;
  background: ${colors.white};
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const ListWrapper = styled.div`
  background: ${colors.white};
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;
